/**
 * client/src/lib/use-removal-hold-push.ts
 *
 * Owner-side removal-hold WebSocket push listener (security tier ladder
 * module 04 AC-25, follow-up to PR #3020; refs #3009, #2270).
 *
 * Listens for `security:removal_hold_opened` and
 * `security:removal_hold_decided` — the two self-scoped frames
 * `server/logic/security-tier-ladder/removal-hold/notify.ts` fans out to every
 * socket of the held account. Until this hook existed the server pushed both
 * frames and NO client rendered them, so the 202 body's "your other signed-in
 * devices were alerted" described a wire event, not a person being told.
 *
 * Modelled directly on `use-recovery-push.ts` (Reuse-First Check,
 * DEVELOPMENT/quality.md Code Review Criteria #8): the same
 * `{type:"auth", userId, sessionId}` handshake and readiness-gate contract.
 *
 * REACH IS NOT GUARANTEED — a WebSocket notice reaches a LIVE session only.
 * The load-bearing discovery path is `GET /api/security/removal-holds`
 * (`RemovalHoldNotice` re-reads it on focus); this listener is defence in
 * depth over it, so a device with the app open learns of the hold without a
 * page change. Durable, persisted delivery to guardians is #3009's scope.
 *
 * This is a notice of an already-committed hold only — never proof about who
 * requested it (Friction Rule clause 2).
 */

import { useCurrentUser } from "@/lib/use-current-user";
import { useReconnectingWs, type UseReconnectingWsResult } from "@/lib/use-reconnecting-ws";

export const REMOVAL_HOLD_OPENED_EVENT = "security:removal_hold_opened" as const;
export const REMOVAL_HOLD_DECIDED_EVENT = "security:removal_hold_decided" as const;
/**
 * The GUARDIAN-addressed frame (#3009). Its own event type, not a shared one,
 * because `WS_EVENT_POLICY` classifies per type and a guardian-only shell must
 * receive this duty while never receiving the owner's account notices.
 *
 * The server sent this before any client listened for it, and before
 * `GET /api/security/removal-holds/guardian-duties` existed — so the frame
 * described a wire event, not a person being told. It is now the LIVE half of a
 * pair: this wakes a session that is already open, and the durable pull feed is
 * what a guardian who was closed, reloaded or offline comes back to. Neither is
 * a receipt, and no copy built on either may say a guardian was alerted.
 */
export const REMOVAL_HOLD_VETO_REQUEST_EVENT = "guardian:removal_hold_veto_request" as const;

export interface RemovalHoldPushFrame extends Record<string, unknown> {
  type:
    | typeof REMOVAL_HOLD_OPENED_EVENT
    | typeof REMOVAL_HOLD_DECIDED_EVENT
    | typeof REMOVAL_HOLD_VETO_REQUEST_EVENT;
  holdId?: string;
  actionKey?: string;
  expiresAt?: string;
  outcome?: string;
  message?: string;
}

/**
 * Listens for removal-hold pushes addressed to the current authenticated
 * user. A null/loading current user opens no socket at all (readiness-gate
 * contract, same as `useRecoveryPush`).
 */
export function useRemovalHoldPush(
  onEvent: (frame: RemovalHoldPushFrame) => void,
): UseReconnectingWsResult {
  const { user } = useCurrentUser();
  return useReconnectingWs<RemovalHoldPushFrame>(
    () =>
      user?.id
        ? { type: "auth", userId: user.id, sessionId: `removal-hold-push-${user.id}` }
        : null,
    (msg) =>
      msg.type === REMOVAL_HOLD_OPENED_EVENT ||
      msg.type === REMOVAL_HOLD_DECIDED_EVENT ||
      msg.type === REMOVAL_HOLD_VETO_REQUEST_EVENT,
    onEvent,
    user?.id,
  );
}
